import { memo, useMemo, useState } from 'react';
import { SidePanelTabs } from 'src/components';
import { BlockContainer } from 'src/components/Layout/named-layout-block.view';
import { PartnerTab } from 'src/components/SidePanelTabs/PartnerTab/partner-tab';
import { Conversation, useGroupRules, UserGroupRules } from 'src/graphql';
import { useGlobalStore } from 'src/store';
import { ConversationDetails } from './ConversationDetails/conversation-details';
import { PartnerHeader } from './partner-header';

const CLOSE_DETAILS_PANEL_TOOLTIP = 'Close Conversation Details';

enum ConversationDetailsTabs {
  Conversation = 'conversation',
  Partner = 'partner',
}

interface TabContentProps {
  conversation: Conversation;
  activeTab: ConversationDetailsTabs;
  onUpdate: (c: Conversation) => Promise<void>;
}

interface ConversationSidePanelProps {
  conversation: Conversation;
  onClose: () => void;
  onUpdate: (c: Conversation) => Promise<void>;
  isConversationExpanded?: boolean;
}

const TabContent = ({ activeTab, conversation, onUpdate }: TabContentProps) => {
  if (activeTab === ConversationDetailsTabs.Conversation) {
    return <ConversationDetails conversation={conversation} onUpdate={onUpdate} />;
  }

  if (activeTab === ConversationDetailsTabs.Partner) {
    return <PartnerTab conversation={conversation} />;
  }

  return null;
};

const ConversationSidePanelComponent = ({
  onClose,
  conversation,
  onUpdate,
  isConversationExpanded,
}: ConversationSidePanelProps) => {
  const isMultiTenancyMode = useGlobalStore.use.isMultiTenancyMode();
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const [activeTab, setActiveTab] = useState(ConversationDetailsTabs.Conversation);
  const { groupRules } = useGroupRules();
  const canAccessToPartnerTab = !!groupRules[UserGroupRules.AccessToPartnerTab];
  const canSeePartnersTab =
    canAccessToPartnerTab || (isMultiTenancyMode && selectedPartnerId === null);

  const tabs = useMemo(
    () =>
      [
        {
          value: ConversationDetailsTabs.Conversation,
          label: 'Conversation',
        },
        {
          value: ConversationDetailsTabs.Partner,
          label: 'Partner',
          skip: !canSeePartnersTab,
          disabled: !conversation.partner,
        },
      ].filter((tab) => !tab.skip),
    [canSeePartnersTab, conversation.partner],
  );

  return (
    <BlockContainer
      rightRoundedOnly
      border={isConversationExpanded ? undefined : 'none !important'}
      sx={{
        height: '100%',
      }}
    >
      <PartnerHeader
        displayPartnerInfo={canAccessToPartnerTab}
        partner={conversation.partner}
        closeDetailsPanelTooltip={CLOSE_DETAILS_PANEL_TOOLTIP}
        onClose={onClose}
      />
      <SidePanelTabs
        sx={{
          overflow: 'auto',
        }}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        tabs={tabs}
        showLabel
        hasBorderTop={false}
      >
        <TabContent activeTab={activeTab} conversation={conversation} onUpdate={onUpdate} />
      </SidePanelTabs>
    </BlockContainer>
  );
};

export const ConversationSidePanel = memo(ConversationSidePanelComponent);
